import {
  decodeJsonFile,
  jsonOutputFileName,
  transformJson,
  validateJsonFile
} from "./json-formatter-core.js";
import { createOperationResult } from "./operation-result.js";
import "./json-formatter.css";

const root = document.querySelector("[data-json-formatter]");

if (root) {
  const elements = Object.fromEntries([
    "jsonFormatterStatus", "jsonFormatterWorkspace", "jsonFormatterFile", "chooseJsonFile",
    "loadJsonExample", "clearJsonFormatter", "jsonFormatterSource", "jsonFormatterSourceMeta",
    "jsonFormatterSourceCount", "jsonFormatterIndentation", "jsonFormatterSortKeys",
    "formatJson", "minifyJson", "jsonFormatterOutput", "jsonFormatterOutputCount",
    "copyFormattedJson", "createJsonFile"
  ].map(id => [id, document.getElementById(id)]));
  const sourceCard = root.querySelector("[data-json-drop]");
  const operationResult = createOperationResult(root, elements.jsonFormatterWorkspace, { trackCompletion: false });
  const state = {
    fileName: "document.json",
    output: "",
    compact: false,
    busy: false
  };

  function showStatus(message = "", kind = "error") {
    elements.jsonFormatterStatus.textContent = message;
    elements.jsonFormatterStatus.dataset.kind = kind;
    elements.jsonFormatterStatus.hidden = !message;
    if (message && kind === "error") {
      window.toolboxAnalytics?.track("tool_error", { error_category: "validation" });
    }
  }

  function countLabel(value) {
    const text = String(value || "");
    const lines = text ? text.split("\n").length : 0;
    return `${text.length.toLocaleString("en-US")} ${text.length === 1 ? "character" : "characters"} · ${lines.toLocaleString("en-US")} ${lines === 1 ? "line" : "lines"}`;
  }

  function updateControls() {
    const hasOutput = Boolean(state.output);
    elements.chooseJsonFile.disabled = state.busy;
    elements.loadJsonExample.disabled = state.busy;
    elements.clearJsonFormatter.disabled = state.busy || (!elements.jsonFormatterSource.value && !hasOutput);
    elements.jsonFormatterSource.disabled = state.busy;
    elements.jsonFormatterIndentation.disabled = state.busy;
    elements.jsonFormatterSortKeys.disabled = state.busy;
    elements.formatJson.disabled = state.busy;
    elements.minifyJson.disabled = state.busy;
    elements.copyFormattedJson.disabled = state.busy || !hasOutput;
    elements.createJsonFile.disabled = state.busy || !hasOutput;
    root.setAttribute("aria-busy", String(state.busy));
  }

  function updateCounts() {
    elements.jsonFormatterSourceCount.textContent = countLabel(elements.jsonFormatterSource.value);
    elements.jsonFormatterOutputCount.textContent = countLabel(state.output);
  }

  function clearOutput() {
    state.output = "";
    elements.jsonFormatterOutput.value = "";
    updateCounts();
    updateControls();
  }

  function sourceChanged() {
    showStatus();
    clearOutput();
    updateCounts();
  }

  function setSource(value, fileName = "document.json") {
    elements.jsonFormatterSource.value = value;
    state.fileName = fileName;
    elements.jsonFormatterSourceMeta.textContent = fileName;
    elements.jsonFormatterSourceMeta.hidden = !fileName;
    sourceChanged();
  }

  async function loadFile(file) {
    const validation = validateJsonFile(file);
    if (validation) return showStatus(validation);
    state.busy = true;
    showStatus();
    updateControls();
    try {
      const source = decodeJsonFile(new Uint8Array(await file.arrayBuffer()));
      setSource(source, file.name || "document.json");
      elements.jsonFormatterSource.focus();
    } catch (error) {
      showStatus(error?.message || "This JSON file could not be read.");
    } finally {
      state.busy = false;
      elements.jsonFormatterFile.value = "";
      updateControls();
    }
  }

  function run(compact) {
    showStatus();
    const result = transformJson(elements.jsonFormatterSource.value, {
      indentation: elements.jsonFormatterIndentation.value,
      sortKeys: elements.jsonFormatterSortKeys.checked,
      compact
    });
    if (result.error) {
      clearOutput();
      showStatus(result.error);
      elements.jsonFormatterSource.focus();
      return;
    }

    state.output = result.output;
    state.compact = compact;
    elements.jsonFormatterOutput.value = result.output;
    updateCounts();
    updateControls();
    showStatus(
      compact
        ? `Valid JSON minified to ${result.characters.toLocaleString("en-US")} characters.`
        : `Valid JSON formatted across ${result.lines.toLocaleString("en-US")} ${result.lines === 1 ? "line" : "lines"}.`,
      "success"
    );
    window.toolboxAnalytics?.track("tool_complete");
    elements.jsonFormatterOutput.focus();
  }

  async function copyOutput() {
    if (!state.output) return;
    try {
      await navigator.clipboard.writeText(state.output);
      showStatus("Formatted JSON copied.", "success");
    } catch {
      showStatus("Copying is unavailable. Select the output and copy it manually.");
    }
  }

  function createFile() {
    if (!state.output) return;
    const fileName = jsonOutputFileName(state.fileName, state.compact);
    operationResult.show({
      blob: new Blob([state.output], { type: "application/json;charset=utf-8" }),
      fileName,
      title: "Your JSON file is ready",
      message: state.compact ? "Your minified JSON is ready to download." : "Your formatted JSON is ready to download.",
      details: [countLabel(state.output)],
      downloadLabel: "Download JSON"
    });
  }

  function clearAll() {
    state.fileName = "document.json";
    elements.jsonFormatterSource.value = "";
    elements.jsonFormatterSourceMeta.textContent = "";
    elements.jsonFormatterSourceMeta.hidden = true;
    showStatus();
    clearOutput();
    updateCounts();
    elements.jsonFormatterSource.focus();
  }

  elements.chooseJsonFile.addEventListener("click", () => elements.jsonFormatterFile.click());
  elements.jsonFormatterFile.addEventListener("change", () => loadFile(elements.jsonFormatterFile.files?.[0]));
  elements.loadJsonExample.addEventListener("click", () => {
    window.toolboxAnalytics?.track("tool_start");
    setSource('{"project":"smallpdf.app","tools":["format","compare"],"active":true}', "example.json");
    run(false);
  });
  elements.clearJsonFormatter.addEventListener("click", clearAll);
  elements.jsonFormatterSource.addEventListener("input", sourceChanged);
  elements.jsonFormatterIndentation.addEventListener("change", sourceChanged);
  elements.jsonFormatterSortKeys.addEventListener("change", sourceChanged);
  elements.formatJson.addEventListener("click", () => run(false));
  elements.minifyJson.addEventListener("click", () => run(true));
  elements.copyFormattedJson.addEventListener("click", copyOutput);
  elements.createJsonFile.addEventListener("click", createFile);

  sourceCard.addEventListener("dragover", event => {
    event.preventDefault();
    sourceCard.classList.add("is-dragging");
  });
  sourceCard.addEventListener("dragleave", () => sourceCard.classList.remove("is-dragging"));
  sourceCard.addEventListener("drop", event => {
    event.preventDefault();
    sourceCard.classList.remove("is-dragging");
    loadFile(event.dataTransfer.files?.[0]);
  });

  window.addEventListener("beforeunload", () => operationResult.dispose());
  updateCounts();
  updateControls();
}
